import { useMemo, useState, type ComponentType } from 'react';
import type { RowClickedEvent } from 'ag-grid-community';
import AgGridWrapper from '@/app/common/components/ag-grid/AgGridWrapper';
import { trackerAiClient as api } from '@/app/TrackerAi/api/trackerAiClient';
import type { PromptExecutionHistoryRow } from '../../types';
import { promptHistoryRunsColumnConfig } from '../../config/promptHistoryRunsColumnConfig';
import { AiChatModal } from './AiChatModal';

type AgGridWrapperProps = {
  table_name?: string;
  height?: string;
  columns: unknown[];
  data: unknown[];
  total?: number;
  settings?: Record<string, unknown>;
  otherOptions?: Record<string, unknown>;
};
const TypedAgGridWrapper = AgGridWrapper as ComponentType<AgGridWrapperProps>;

export function PromptHistoryRunsTable({
  rows,
  onViewExecution,
}: {
  rows: PromptExecutionHistoryRow[];
  onViewExecution?: (executionId: string) => void;
}) {
  const competitorColumns = useMemo(
    () =>
      rows.reduce<string[]>((names, row) => {
        for (const competitor of row.competitors) {
          if (!names.includes(competitor.name)) names.push(competitor.name);
        }
        return names;
      }, []),
    [rows],
  );

  const [chat, setChat] = useState<{
    promptText: string;
    responseText: string;
    modelLabel?: string;
    modelIconSvg?: string | null;
  } | null>(null);
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [responseError, setResponseError] = useState<string | null>(null);

  const columns = useMemo(
    () =>
      promptHistoryRunsColumnConfig({
        competitorColumns,
        loadingResponseId: loadingId,
        onViewResponse: row => {
          void openResponse(row);
        },
      }),
    [competitorColumns, loadingId],
  );

  async function openResponse(row: PromptExecutionHistoryRow) {
    if (loadingId) return;
    setLoadingId(row.id);
    setResponseError(null);
    try {
      const insight = await api.getExecutionInsight(row.id);
      setChat({
        promptText: insight.promptText,
        responseText: insight.responseText ?? '',
        modelLabel: row.modelName ?? insight.model ?? undefined,
        modelIconSvg: row.modelIconSvg ?? insight.modelIconSvg,
      });
    } catch (error) {
      setResponseError(error instanceof Error ? error.message : 'بارگذاری پاسخ ممکن نشد.');
    } finally {
      setLoadingId(null);
    }
  }

  const otherOptions = useMemo(
    () =>
      onViewExecution
        ? {
            onRowClicked: (event: RowClickedEvent<PromptExecutionHistoryRow>) => {
              const target = event.event?.target;
              if (target instanceof Element && target.closest('button')) return;
              if (event.data) onViewExecution(event.data.id);
            },
            rowClass: 'cursor-pointer',
          }
        : undefined,
    [onViewExecution],
  );

  if (!rows.length) {
    return (
      <p className="fs--1 text-500 text-center py-4 mb-0">
        در این بازه اجرای موفقی برای این پرامپت ثبت نشده است.
      </p>
    );
  }

  return (
    <>
      {responseError ? <p className="fs--1 text-danger mb-2">{responseError}</p> : null}
      <TypedAgGridWrapper
        table_name="tracker_ai_prompt_history_runs"
        height="auto"
        columns={columns}
        data={rows}
        total={rows.length}
        settings={{ pagination: false, fullscreen: false, sideBar: false, export: { enabled: false } }}
        otherOptions={otherOptions}
      />
      {chat ? (
        <AiChatModal
          open
          onClose={() => setChat(null)}
          promptText={chat.promptText}
          responseText={chat.responseText}
          modelLabel={chat.modelLabel}
          modelIconSvg={chat.modelIconSvg}
        />
      ) : null}
    </>
  );
}
